<template>
  <transition name="toast-fade">
    <div
      v-if="visible"
      class="fixed top-6 right-6 z-[9999] max-w-[500px]"
      role="alert"
    >
      <div
        :class="[
          'px-4 py-4 rounded-[8px] shadow-lg border flex items-start gap-3',
          typeClass
        ]"
      >
        <div class="flex-shrink-0">
          <!-- Success Icon -->
          <svg
            v-if="type === 'success'"
            width="35"
            height="35"
            viewBox="0 0 35 35"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g opacity="0.3">
              <rect x="4.99984" y="4.99984" width="25" height="25" rx="12.5" stroke="#079455" stroke-width="1.66667"/>
            </g>
            <g opacity="0.1">
              <rect x="0.833333" y="0.833333" width="33.3333" height="33.3333" rx="16.6667" stroke="#079455" stroke-width="1.66667"/>
            </g>
            <g clip-path="url(#clip0_success)">
              <path d="M13.7498 17.4998L16.2498 19.9998L21.2498 14.9998M25.8332 17.4998C25.8332 22.1022 22.1022 25.8332 17.4998 25.8332C12.8975 25.8332 9.1665 22.1022 9.1665 17.4998C9.1665 12.8975 12.8975 9.1665 17.4998 9.1665C22.1022 9.1665 25.8332 12.8975 25.8332 17.4998Z" stroke="#079455" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_success">
                <rect width="20" height="20" fill="white" transform="translate(7.5 7.5)"/>
              </clipPath>
            </defs>
          </svg>

          <!-- Error/Warning Icon -->
          <svg
            v-else-if="type === 'error' || type === 'warning'"
            width="38"
            height="38"
            viewBox="0 0 38 38"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g opacity="0.3">
              <rect x="6" y="6" width="26" height="26" rx="13" stroke="#D92D20" stroke-width="2"/>
            </g>
            <g opacity="0.1">
              <rect x="1" y="1" width="36" height="36" rx="18" stroke="#D92D20" stroke-width="2"/>
            </g>
            <g clip-path="url(#clip0_error)">
              <path d="M18.9998 15.6665V18.9998M18.9998 22.3332H19.0082M27.3332 18.9998C27.3332 23.6022 23.6022 27.3332 18.9998 27.3332C14.3975 27.3332 10.6665 23.6022 10.6665 18.9998C10.6665 14.3975 14.3975 10.6665 18.9998 10.6665C23.6022 10.6665 27.3332 14.3975 27.3332 18.9998Z" stroke="#D92D20" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_error">
                <rect width="20" height="20" fill="white" transform="translate(9 9)"/>
              </clipPath>
            </defs>
          </svg>

          <!-- Info Icon -->
          <svg
            v-else
            width="35"
            height="35"
            viewBox="0 0 35 35"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g opacity="0.3">
              <rect x="4.99984" y="4.99984" width="25" height="25" rx="12.5" stroke="#3B82F6" stroke-width="1.66667"/>
            </g>
            <g opacity="0.1">
              <rect x="0.833333" y="0.833333" width="33.3333" height="33.3333" rx="16.6667" stroke="#3B82F6" stroke-width="1.66667"/>
            </g>
            <g clip-path="url(#clip0_info)">
              <path d="M17.5 16.25V21.25M17.5 13.75H17.5083M25.8332 17.5C25.8332 22.1024 22.1022 25.8333 17.5 25.8333C12.8976 25.8333 9.16667 22.1024 9.16667 17.5C9.16667 12.8976 12.8976 9.16667 17.5 9.16667C22.1022 9.16667 25.8332 12.8976 25.8332 17.5Z" stroke="#3B82F6" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_info">
                <rect width="20" height="20" fill="white" transform="translate(7.5 7.5)"/>
              </clipPath>
            </defs>
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-[14px] font-semibold" :class="titleClass">
            {{ title }}
          </p>
          <p v-if="message" class="text-[14px] mt-1" :class="messageClass">
            {{ message }}
          </p>
        </div>
        <button
          @click="close"
          class="flex-shrink-0 ml-2 hover:opacity-70 transition-opacity"
          :class="closeButtonClass"
          aria-label="Close"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13M1 1L13 13"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "ToastNotification",
  data() {
    return {
      visible: false,
      type: "info", // success, error, warning, info
      title: "",
      message: "",
      duration: 5000,
      timer: null,
    };
  },
  computed: {
    typeClass() {
      const classes = {
        success: "bg-[#ECFDF3] border-[#ABEFC6]",
        error: "bg-[#FEF3F2] border-[#FECDCA]",
        warning: "bg-[#FEF3F2] border-[#FECDCA]",
        info: "bg-blue-50 border-blue-200",
      };
      return classes[this.type] || classes.info;
    },
    titleClass() {
      const classes = {
        success: "text-[#027A48]",
        error: "text-[#B42318]",
        warning: "text-[#B42318]",
        info: "text-blue-800",
      };
      return classes[this.type] || classes.info;
    },
    messageClass() {
      const classes = {
        success: "text-[#027A48]",
        error: "text-[#B42318]",
        warning: "text-[#B42318]",
        info: "text-blue-700",
      };
      return classes[this.type] || classes.info;
    },
    closeButtonClass() {
      const classes = {
        success: "text-[#027A48]",
        error: "text-[#B42318]",
        warning: "text-[#B42318]",
        info: "text-blue-800",
      };
      return classes[this.type] || classes.info;
    },
  },
  methods: {
    show(options) {
      this.type = options.type || "info";
      this.title = options.title || "";
      this.message = options.message || "";
      this.duration = options.duration || 5000;
      this.visible = true;

      if (this.timer) {
        clearTimeout(this.timer);
      }

      if (this.duration > 0) {
        this.timer = setTimeout(() => {
          this.close();
        }, this.duration);
      }
    },
    close() {
      this.visible = false;
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
  },
  beforeUnmount() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  },
};
</script>

<style scoped>
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.3s ease;
}

.toast-fade-enter-from {
  opacity: 0;
  transform: translateX(100px);
}

.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
